import { z } from 'zod'

// Read-only analytics over dispatch sessions/outcomes/lookups. One bundle,
// computed live in @rapid-response/api `getDispatchAnalytics`. Shared by the
// REST route (/api/v1/analytics) and the RPC handler in packages/web.

export const analyticsBucketSchema = z.enum(['day', 'week'])
export type AnalyticsBucket = z.infer<typeof analyticsBucketSchema>

// Query params (REST). `from`/`to` are ISO timestamps; both optional — the
// server defaults to the last 30 days when omitted.
export const analyticsQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  bucket: analyticsBucketSchema.default('day'),
  driveMins: z.coerce.number().int().positive().default(30),
  freshMins: z.coerce.number().int().positive().default(10),
})
export type AnalyticsQuery = z.infer<typeof analyticsQuerySchema>

export const analyticsRangeSchema = z.object({
  from: z.string(), // ISO
  to: z.string(), // ISO
  bucket: analyticsBucketSchema,
})

export const analyticsTotalsSchema = z.object({
  sessions: z.number().int(),
  activeUsers: z.number().int(),
  assets: z.number().int(),
  lookups: z.number().int(),
  outcomes: z.number().int(),
  accepted: z.number().int(),
  acceptanceRate: z.number(), // 0..1; 0 when no outcomes
})

export const analyticsActivityPointSchema = z.object({
  bucket: z.string(), // 'YYYY-MM-DD'
  sessions: z.number().int(),
})

export const analyticsDataUsagePointSchema = z.object({
  bucket: z.string(),
  ok: z.number().int(),
  error: z.number().int(),
  results: z.number().int(),
})

export const analyticsFunnelSchema = z.object({
  sessionsRun: z.number().int(),
  sessionsWithOutcome: z.number().int(),
  outcomesAccepted: z.number().int(),
  distinctAcceptedJobs: z.number().int(),
})

export const analyticsUserRowSchema = z.object({
  userId: z.string().nullable(),
  name: z.string(),
  sessions: z.number().int(),
  outcomes: z.number().int(),
  accepted: z.number().int(),
})

export const analyticsTargetRowSchema = z.object({
  targetKey: z.string(),
  label: z.string(),
  accepted: z.number().int(),
  declined: z.number().int(),
  callAttempted: z.number().int(),
})

export const coverageOutcodeRowSchema = z.object({
  outcode: z.string(),
  searches: z.number().int(),
  avgOfficers: z.number(),
  avgWithinDriveTime: z.number(),
  avgFresh: z.number(),
})

export const coverageTrendPointSchema = z.object({
  bucket: z.string(),
  avgOfficers: z.number(),
  avgFresh: z.number(),
})

export const analyticsCoverageSchema = z.object({
  driveMins: z.number().int(),
  freshMins: z.number().int(),
  avgOfficers: z.number(),
  avgWithinDriveTime: z.number(),
  avgFresh: z.number(),
  byOutcode: z.array(coverageOutcodeRowSchema),
  trend: z.array(coverageTrendPointSchema),
})
export type AnalyticsCoverage = z.infer<typeof analyticsCoverageSchema>
export type CoverageOutcodeRow = z.infer<typeof coverageOutcodeRowSchema>

// Append-only activity-log counts (RRP-0025): the search → recommendation →
// called → accepted/declined funnel, plus un-marks. Event counts, so retries
// and undos are visible (unlike the upsert-state `funnel` above).
export const analyticsActivityFunnelSchema = z.object({
  searches: z.number().int(),
  recommendationsOfficers: z.number().int(),
  recommendationsJobs: z.number().int(),
  called: z.number().int(),
  accepted: z.number().int(),
  declined: z.number().int(),
  cleared: z.number().int(),
})
export type AnalyticsActivityFunnel = z.infer<typeof analyticsActivityFunnelSchema>

export const dispatchAnalyticsSchema = z.object({
  range: analyticsRangeSchema,
  totals: analyticsTotalsSchema,
  activity: z.array(analyticsActivityPointSchema),
  dataUsage: z.array(analyticsDataUsagePointSchema),
  funnel: analyticsFunnelSchema,
  activityFunnel: analyticsActivityFunnelSchema,
  users: z.array(analyticsUserRowSchema),
  officers: z.array(analyticsTargetRowSchema),
  jobs: z.array(analyticsTargetRowSchema),
  coverage: analyticsCoverageSchema,
})
export type DispatchAnalytics = z.infer<typeof dispatchAnalyticsSchema>
export type AnalyticsUserRow = z.infer<typeof analyticsUserRowSchema>
export type AnalyticsTargetRow = z.infer<typeof analyticsTargetRowSchema>

/** UK postcode → outcode (the outward code). 'SW1A 1AA' → 'SW1A'. Nullish or
 * unparseable → 'Unknown'. Isolated so the region granularity is one change. */
export function regionOf(postcode: string | null | undefined): string {
  if (!postcode) return 'Unknown'
  const pc = postcode.trim().toUpperCase().split(/\s+/).join(' ')
  if (!pc) return 'Unknown'
  if (pc.includes(' ')) return pc.split(' ')[0]
  // No space: UK inward code is always the last 3 chars.
  return pc.length > 3 ? pc.slice(0, -3) : pc
}

/** Per-search operative counts: total, within drive-time, and "fresh" (last
 * location update within `freshMs` of the lookup time, not now). */
export function summariseOperatives(
  operatives: ReadonlyArray<{ durationSeconds: number; lastSeen: string | null }>,
  lookupAtMs: number,
  driveSecs: number,
  freshMs: number,
): { total: number; withinDriveTime: number; fresh: number } {
  let withinDriveTime = 0
  let fresh = 0
  for (const op of operatives) {
    if (op.durationSeconds <= driveSecs) withinDriveTime += 1
    if (op.lastSeen) {
      const seen = Date.parse(op.lastSeen)
      if (!Number.isNaN(seen) && lookupAtMs - seen <= freshMs) fresh += 1
    }
  }
  return { total: operatives.length, withinDriveTime, fresh }
}

/** Bucket a date like Postgres date_trunc (UTC): day → 'YYYY-MM-DD', week →
 * the Monday of that week as 'YYYY-MM-DD'. */
export function coverageBucketKey(d: Date, bucket: AnalyticsBucket): string {
  if (bucket === 'week') {
    const dt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
    const mondayOffset = (dt.getUTCDay() + 6) % 7
    dt.setUTCDate(dt.getUTCDate() - mondayOffset)
    return dt.toISOString().slice(0, 10)
  }
  return d.toISOString().slice(0, 10)
}
